import { Routes } from '@angular/router';
import { AuthGuard } from './core/guards/authguard';
import { ExternalUserAccessGuard } from './core/guards/external-user-access.guard';
import { workflowFillGuard } from './core/guards/workflow-fill.guard';

export const routes: Routes = [
  // 🔑 Login (outside home layout)
  {
    path: 'login',
    loadComponent: () => import('./pages/login/login').then((m) => m.Login),
  },
  {
    path: 'login/password-recovery',
    loadComponent: () => import('./pages/login/password-recovery/password-recovery').then(m => m.PasswordRecovery)
  },
  {
    path: 'password-recovery/:token',
    loadComponent: () => import('./pages/login/reset-password/reset-password').then(m => m.ResetPassword)
  },

  // 📝 Supplier Pre-Registration: formulário público (componente New em supplier/pre-register/new), sem workflow/API de definições
  {
    path: 'registro/fornecedor',
    loadComponent: () => import('./pages/supplier/pre-register/new/new').then((m) => m.New),
  },

  // 📋 Formulários públicos (acesso sem login quando workflow é público)
  {
    path: 'form/:workflowSlug',
    canActivate: [workflowFillGuard],
    loadComponent: () =>
      import('@src/app/pages/workflows/fill/fill').then((m) => m.WorkflowFillShellComponent),
  },
  {
    path: 'form/:workflowSlug/:instanceId',
    canActivate: [workflowFillGuard],
    loadComponent: () =>
      import('@src/app/pages/workflows/fill/fill').then((m) => m.WorkflowFillShellComponent),
  },

  // 🏠 Home Layout with nested routes
  {
    path: '',
    loadComponent: () => import('./pages/home/home').then((m) => m.Home),
    canActivate: [AuthGuard],
    canActivateChild: [ExternalUserAccessGuard],
    children: [
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./pages/dashboard/dashboard.component').then((m) => m.DashboardComponent),
      },

      {
        path: 'release-notes',
        loadComponent: () =>
          import('./pages/release-notes/release-notes-page').then((m) => m.ReleaseNotesPage),
        data: { title: 'Notas de versão' },
      },

      {
        path: 'user-profile',
        loadComponent: () => import('./pages/user-profile/user-profile.component').then(m => m.UserProfilePageComponent),
      },

      // 📦 Supplier Module
      {
        path: 'suppliers',
        loadChildren: () =>
          import('./pages/supplier/supplier.routes').then((m) => m.SUPPLIER_ROUTES),
      },

      // 👥 Access Module
      {
        path: 'access',
        loadChildren: () => import('./pages/access/access.routes').then((m) => m.ACCESS_ROUTES),
      },

      // 🛍️ Product Module
      {
        path: 'products',
        loadChildren: () => import('./pages/product/product.routes').then((m) => m.PRODUCT_ROUTES),
      },

      {
        path: 'catalog',
        children: [
          {
            path: 'purchase-groups',
            loadChildren: () =>
              import('./pages/catalog-purchase-group/catalog-purchase-group.routes').then(
                (m) => m.CATALOG_PURCHASE_GROUP_ROUTES
              ),
          },
        ],
      },

      /** @deprecated URLs antigas → `/catalog/purchase-groups`; remover após transição */
      {
        path: 'catalog-purchase-groups',
        redirectTo: '/catalog/purchase-groups',
        pathMatch: 'full',
      },
      {
        path: 'catalog-purchase-groups/new',
        redirectTo: '/catalog/purchase-groups/new',
        pathMatch: 'full',
      },
      {
        path: 'catalog-purchase-groups/products-without-group',
        redirectTo: '/catalog/purchase-groups/products-without-group',
        pathMatch: 'full',
      },
      {
        path: 'catalog-purchase-groups/detail/:id',
        redirectTo: '/catalog/purchase-groups/detail/:id',
      },

      // 📋 Pre-Product Module
      {
        path: 'pre-products',
        loadChildren: () =>
          import('./pages/pre-product/pre-product.routes').then((m) => m.PRE_PRODUCT_ROUTES),
      },

      // 📥 SellIn Module
      {
        path: 'sellin',
        loadChildren: () => import('./pages/sell-in/sell-in.routes').then((m) => m.SELL_IN_ROUTES),
      },

      // 📊 SellOut Module
      {
        path: 'sellout',
        loadChildren: () =>
          import('./pages/sell-out/sell-out.routes').then((m) => m.SELL_OUT_ROUTES),
      },

      // 📦 Stock Module
      {
        path: 'stock',
        loadChildren: () => import('./pages/stock/stock.routes').then((m) => m.STOCK_ROUTES),
      },

      // 🏪 Store Module
      {
        path: 'store',
        loadChildren: () => import('./pages/store/store.routes').then((m) => m.STORE_ROUTES),
      },

      // 🗂️ Department Module
      {
        path: 'departments',
        loadChildren: () =>
          import('./pages/department/department.routes').then((m) => m.DEPARTMENT_ROUTES),
      },

      // 🏷️ Brand Module
      {
        path: 'brands',
        loadChildren: () => import('./pages/brand/brand.routes').then((m) => m.BRAND_ROUTES),
      },

      // 🔄 ExchangeReturns Module
      {
        path: 'exchange-returns',
        loadChildren: () =>
          import('./pages/exchange-returns/exchange-returns.routes').then(
            (m) => m.EXCHANGE_RETURNS_ROUTES
          ),
      },

      // 📰 News Module
      {
        path: 'news',
        loadChildren: () => import('./pages/news/news.routes').then((m) => m.NEWS_ROUTES),
      },

      // 📈 Reports Module
      {
        path: 'reports',
        loadChildren: () => import('./pages/reports/reports.routes').then((m) => m.REPORTS_ROUTES),
      },

      // ⚙️ Settings Module
      {
        path: 'settings',
        loadChildren: () =>
          import('./pages/settings/settings.routes').then((m) => m.SETTINGS_ROUTES),
      },

      // 📐 Workflows (configuração / builder)
      {
        path: 'workflows',
        loadChildren: () =>
          import('./pages/workflows/workflows.routes').then((m) => m.WORKFLOWS_ROUTES),
      },

      /** Legado: URLs curtas /approve-v2 → /workflows/approve */
      {
        path: 'approve-v2',
        redirectTo: '/workflows/approve',
        pathMatch: 'full',
      },
      {
        path: 'approve-v2/:workflowSlug/:instanceId',
        redirectTo: '/workflows/approve/:workflowSlug/:instanceId',
      },

      // 🔌 ERP Integration Monitoring
      {
        path: 'integration-monitoring',
        loadChildren: () =>
          import('./pages/integration-monitoring/integration-monitoring.routes').then(
            (m) => m.INTEGRATION_MONITORING_ROUTES,
          ),
      },

      // 🗄️ Dados de staging do ERP (grids das tabelas Erp_*) — somente ADMINISTRATOR
      {
        path: 'erp-staging',
        loadChildren: () =>
          import('./pages/erp-staging/erp-staging.routes').then((m) => m.ERP_STAGING_ROUTES),
      },

      // ✉️ In-app Messages (broadcast)
      {
        path: 'messages',
        loadChildren: () =>
          import('./pages/messages/messages.routes').then((m) => m.MESSAGES_ROUTES),
      },

      // 🔔 Notificações
      {
        path: 'notifications',
        loadComponent: () =>
          import('./pages/notifications').then((m) => m.NotificationsPage),
      },

      // Default redirect to dashboard
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
    ],
  },

  // Catch-all redirect
  { path: '**', redirectTo: '' },
];
